import React from 'react';
import Joi from 'joi';
import getConfig from 'next/config';
import _ from 'underscore';
import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';
import { addQuery, filterQuery } from '@culture/common-helpers/url';

import Template from 'pageTemplates/music/musicalGroups';

const CollectivesCatalogPage = (props) => <Template {...props} />;

const { serverRuntimeConfig } = getConfig();
const rubricPath = 'music/musical-groups';

const getBreadcrumbs = (cleanCache) =>
	apiRequest(
		'breadcrumbs',
		addCleanCache(
			{
				rubricPath,
				entityType: 'collectives',
				cached: true
			},
			cleanCache
		)
	);

const getCollectives = (queryParams, currentRubricId, cleanCache) => {
	const params = filterQuery({
		...queryParams,
		cached: true,
		statuses: ['published'],
		fields: ['title', 'thumbnailFile', 'city', 'federalSubject', 'name'].join(),
		rubrics: [currentRubricId],
		limit: 16
	});

	return apiRequest('collectives', addCleanCache(params, cleanCache));
};

const getMeta = ({ page, cleanCache }) =>
	apiRequest(
		'meta/catalog',
		addCleanCache(
			{
				page,
				rubricPath,
				cached: true
			},
			cleanCache
		)
	);

const getRubricsTree = (cleanCache) =>
	apiRequest(
		'rubrics/tree',
		addCleanCache(
			{
				rootPath: rubricPath,
				entityType: 'collectives'
			},
			cleanCache
		)
	);

export async function getServerSideProps({ query, resolvedUrl }) {
	const defaultSort = '-views';
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean().failover(false),
		query: Joi.string(),
		page: Joi.number().default(1).min(1),
		sort: Joi.string()
			.valid('views', '-views', 'publishDate', '-publishDate')
			.failover(defaultSort)
			.default(defaultSort)
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const { cleanCache, page } = params;

	const filteredQueryParams = filterQuery(
		_(params).pick('query', 'sort', 'page', 'cleanCache')
	);

	const rubrics = await getRubricsTree(cleanCache);
	const rubric = rubrics[0];

	const requests = [
		getCollectives(filteredQueryParams, rubric?._id, cleanCache),
		getMeta({ page, cleanCache }),
		getBreadcrumbs(cleanCache)
	];
	const [collectives, meta, breadcrumbs] = await batchApiRequest(requests);

	if (
		collectives.pagination.total &&
		collectives.pagination.total < params.page
	) {
		return {
			redirect: {
				destination: addQuery(`${serverRuntimeConfig.baseUrl}${resolvedUrl}`, {
					page: collectives.pagination.total
				}),
				permanent: false
			}
		};
	}

	return {
		props: {
			collectives,
			meta,
			breadcrumbs,
			urlParams: filteredQueryParams,
			defaultUrlParams: {
				sort: defaultSort
			},
			rubric: { rootRubricPath: rubricPath, name: rubric?.name }
		}
	};
}

export default CollectivesCatalogPage;
